import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Link,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  MapContainer,
  Marker,
  TileLayer,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { supabase } from "../supabaseClient";

const SERBIA_CENTER = [44.0165, 21.0059];

const FALLBACK_AVATAR =
  "https://api.dicebear.com/8.x/initials/svg?seed=MeetOutdoors";

const FALLBACK_COVER =
  "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=1800&auto=format&fit=crop";

function Icon({ name, size = 20, strokeWidth = 2 }) {
  const icons = {
    compass: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m15.5 8.5-2 5-5 2 2-5 5-2Z" />
      </>
    ),
    edit: (
      <>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" />
      </>
    ),
    mapPin: (
      <>
        <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
        <circle cx="12" cy="10" r="2.5" />
      </>
    ),
    user: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0" />
      </>
    ),
    shield: (
      <>
        <path d="M12 3 5 6v5c0 4.6 2.9 8.4 7 10 4.1-1.6 7-5.4 7-10V6l-7-3Z" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),
    sparkle: (
      <>
        <path d="m12 3 1.1 3.3L16 8l-2.9 1.7L12 13l-1.1-3.3L8 8l2.9-1.7L12 3Z" />
        <path d="m18 14 .7 2.3L21 17l-2.3.7L18 20l-.7-2.3L15 17l2.3-.7L18 14Z" />
      </>
    ),
    arrowRight: (
      <>
        <path d="M5 12h14" />
        <path d="m13 6 6 6-6 6" />
      </>
    ),
    arrowLeft: (
      <>
        <path d="M19 12H5" />
        <path d="m11 18-6-6 6-6" />
      </>
    ),
    alert: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v5M12 16h.01" />
      </>
    ),
    refresh: (
      <>
        <path d="M20 7v5h-5M4 17v-5h5" />
        <path d="M18.4 9A7 7 0 0 0 6 6.5L4 9M5.6 15A7 7 0 0 0 18 17.5l2-2.5" />
      </>
    ),
    trophy: (
      <>
        <path d="M8 4h8v5a4 4 0 0 1-8 0Z" />
        <path d="M8 6H4v2a4 4 0 0 0 4 4M16 6h4v2a4 4 0 0 1-4 4" />
        <path d="M12 13v4M8 21h8M9 17h6" />
      </>
    ),
    camera: (
      <>
        <path d="M14.5 4 16 6h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3l1.5-2Z" />
        <circle cx="12" cy="12" r="3.5" />
      </>
    ),
    heart: (
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z" />
    ),
    route: (
      <>
        <circle cx="6" cy="18" r="2" />
        <circle cx="18" cy="6" r="2" />
        <path d="M8 18h2a4 4 0 0 0 4-4v-4a4 4 0 0 1 4-4" />
      </>
    ),
    verified: (
      <>
        <path d="M12 3l2.2 1.7 2.8-.2.9 2.7 2.3 1.6-1 2.6.7 2.7-2.4 1.4-.6 2.8-2.8-.1L12 20l-2.1-1.8-2.8.1-.6-2.8-2.4-1.4.7-2.7-1-2.6 2.3-1.6.9-2.7 2.8.2Z" />
        <path d="m8.5 12 2.2 2.2 4.8-5" />
      </>
    ),
    layers: (
      <>
        <path d="m12 3 8 4-8 4-8-4 8-4Z" />
        <path d="m4 12 8 4 8-4M4 17l8 4 8-4" />
      </>
    ),
    grid: (
      <>
        <rect x="4" y="4" width="6" height="6" rx="1.5" />
        <rect x="14" y="4" width="6" height="6" rx="1.5" />
        <rect x="4" y="14" width="6" height="6" rx="1.5" />
        <rect x="14" y="14" width="6" height="6" rx="1.5" />
      </>
    ),
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {icons[name]}
    </svg>
  );
}

function formatDate(value) {
  if (!value) return "";

  return new Intl.DateTimeFormat("sr-Latn-RS", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

function formatNumber(value) {
  return new Intl.NumberFormat("sr-Latn-RS").format(
    Number(value || 0)
  );
}

function getLevel(visitedCount) {
  const count = Number(visitedCount || 0);

  if (count >= 150) {
    return {
      name: "Legend",
      next: null,
      min: 150,
      max: 150,
    };
  }

  if (count >= 71) {
    return {
      name: "Nomad",
      next: "Legend",
      min: 71,
      max: 150,
    };
  }

  if (count >= 31) {
    return {
      name: "Pathfinder",
      next: "Nomad",
      min: 31,
      max: 71,
    };
  }

  if (count >= 11) {
    return {
      name: "Adventurer",
      next: "Pathfinder",
      min: 11,
      max: 31,
    };
  }

  return {
    name: "Explorer",
    next: "Adventurer",
    min: 0,
    max: 11,
  };
}

function makePassportMarker(place) {
  const image = place?.cover_url || FALLBACK_COVER;

  return L.divIcon({
    className: "passportMarkerShell",
    html: `
      <div class="passportMarker">
        <img src="${image}" alt="" />
        <span></span>
      </div>
    `,
    iconSize: [46, 54],
    iconAnchor: [23, 48],
  });
}

function LoadingState() {
  return (
    <>
      <UserProfileStyles />

      <main className="userPassportStatePage">
        <div className="userPassportStateCard">
          <span className="userPassportLoader" />
          <h1>Učitavanje profila</h1>
          <p>
            Pripremamo Outdoor Passport ovog člana.
          </p>
        </div>
      </main>
    </>
  );
}

export default function UserProfile() {
  const { username } = useParams();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  const [checkins, setCheckins] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [savedPlaces, setSavedPlaces] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError) {
        console.warn(
          "Greška pri proveri korisnika:",
          authError
        );
      }

      setCurrentUserId(user?.id || null);

      const {
        data: userProfile,
        error: profileError,
      } = await supabase
        .from("profiles")
        .select("*")
        .eq("username", username)
        .eq("role", "user")
        .single();

      if (profileError) {
        throw profileError;
      }

      setProfile(userProfile);

      const [
        checkinsResult,
        photosResult,
        savesResult,
      ] = await Promise.all([
        supabase
          .from("place_checkins")
          .select(`
            id,
            place_id,
            visited_at,
            created_at,
            is_gps_verified,
            visibility,
            review_status,
            places:place_id!inner (
              id,
              name,
              latitude,
              longitude,
              locality,
              region,
              cover_url,
              is_active,
              place_categories:category_id (
                id,
                name,
                code
              )
            )
          `)
          .eq("user_id", userProfile.id)
          .eq("is_gps_verified", true)
          .eq("visibility", "public")
          .eq("review_status", "approved")
          .eq("places.is_active", true)
          .order("created_at", {
            ascending: false,
          })
          .limit(500),

        supabase
          .from("place_photos")
          .select(`
            id,
            place_id,
            checkin_id,
            image_url,
            caption,
            created_at,
            moderation_status,
            places:place_id!inner (
              id,
              name,
              cover_url,
              locality,
              region,
              is_active
            )
          `)
          .eq("user_id", userProfile.id)
          .eq("moderation_status", "approved")
          .eq("places.is_active", true)
          .order("created_at", {
            ascending: false,
          })
          .limit(100),

        supabase
          .from("place_saves")
          .select(`
            id,
            place_id,
            created_at,
            places:place_id!inner (
              id,
              name,
              cover_url,
              locality,
              region,
              latitude,
              longitude,
              is_active,
              place_categories:category_id (
                id,
                name,
                code
              )
            )
          `)
          .eq("user_id", userProfile.id)
          .eq("places.is_active", true)
          .order("created_at", {
            ascending: false,
          })
          .limit(100),
      ]);

      const validCheckins = checkinsResult.error
        ? []
        : checkinsResult.data || [];

      const validCheckinIds = new Set(
        validCheckins.map((item) => item.id)
      );

      if (!checkinsResult.error) {
        setCheckins(validCheckins);
      } else {
        console.warn(
          "User checkins:",
          checkinsResult.error
        );
        setCheckins([]);
      }

      if (!photosResult.error) {
        setPhotos(
          (photosResult.data || []).filter(
            (photo) =>
              !photo.checkin_id ||
              validCheckinIds.has(photo.checkin_id)
          )
        );
      } else {
        console.warn(
          "User photos:",
          photosResult.error
        );
        setPhotos([]);
      }

      if (!savesResult.error) {
        setSavedPlaces(savesResult.data || []);
      } else {
        console.warn(
          "User saves:",
          savesResult.error
        );
        setSavedPlaces([]);
      }
    } catch (err) {
      console.error(
        "Greška pri učitavanju profila:",
        err
      );

      setProfile(null);
      setCheckins([]);
      setPhotos([]);
      setSavedPlaces([]);

      setError(
        err.message ||
          "Korisnički profil trenutno nije moguće učitati."
      );
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    const refreshProfile = () => {
      loadProfile();
    };

    const channel = supabase
      .channel(`user-profile-live-${username}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "places",
        },
        refreshProfile
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "place_checkins",
        },
        refreshProfile
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "place_photos",
        },
        refreshProfile
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "place_saves",
        },
        refreshProfile
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadProfile, username]);

  const isOwnProfile =
    currentUserId === profile?.id;

  const displayName =
    profile?.full_name ||
    "MeetOutdoors User";

  const location = [
    profile?.city,
    profile?.country,
  ]
    .filter(Boolean)
    .join(", ");

  const activities = useMemo(
    () =>
      Array.isArray(profile?.activities)
        ? profile.activities
        : [],
    [profile?.activities]
  );

  const visitedPlaces = useMemo(() => {
    const unique = new Map();

    checkins.forEach((checkin) => {
      const place = checkin.places;

      if (
        place?.id &&
        !unique.has(place.id)
      ) {
        unique.set(place.id, {
          ...place,
          latest_visit:
            checkin.visited_at ||
            checkin.created_at,
        });
      }
    });

    return Array.from(unique.values());
  }, [checkins]);



  const level = useMemo(
    () => getLevel(visitedPlaces.length),
    [visitedPlaces.length]
  );



  const mapPlaces = useMemo(
    () =>
      visitedPlaces.filter(
        (place) =>
          Number.isFinite(
            Number(place.latitude)
          ) &&
          Number.isFinite(
            Number(place.longitude)
          )
      ),
    [visitedPlaces]
  );

  const mapCenter = useMemo(() => {
    if (mapPlaces.length === 0) {
      return SERBIA_CENTER;
    }

    return [
      Number(mapPlaces[0].latitude),
      Number(mapPlaces[0].longitude),
    ];
  }, [mapPlaces]);

  const recentCheckins = useMemo(
    () => checkins.slice(0, 8),
    [checkins]
  );



  if (loading) {
    return <LoadingState />;
  }

  if (!profile) {
    return (
      <>
        <UserProfileStyles />

        <main className="userPassportStatePage">
          <div className="userPassportStateCard">
            <span className="userPassportStateIcon">
              <Icon name="alert" size={28} />
            </span>

            <h1>Profil nije pronađen</h1>

            <p>
              {error ||
                "Ovaj korisnički profil ne postoji ili više nije dostupan."}
            </p>

            <div className="userPassportStateActions">
              <button
                type="button"
                onClick={loadProfile}
              >
                <Icon
                  name="refresh"
                  size={16}
                />
                Pokušaj ponovo
              </button>

              <Link to="/">
                <Icon
                  name="arrowLeft"
                  size={16}
                />
                Početna
              </Link>
            </div>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <UserProfileStyles />

      <main className="userPassportPage">
        <section
          className="userPassportHero"
          style={{
            backgroundImage: `linear-gradient(
              180deg,
              rgba(6,21,13,.12),
              rgba(6,21,13,.88)
            ), url(${profile.cover_url || FALLBACK_COVER})`,
          }}
        >
          <div className="userPassportHeroGlow" />

          <div className="userPassportHeroTop">
            <Link
              to="/explore"
              className="userPassportBrand"
            >
              <span>
                <Icon
                  name="compass"
                  size={21}
                />
              </span>

              MeetOutdoors
            </Link>

            <Link
              to="/explore"
              className="userPassportExploreLink"
            >
              Explore mapa
              <Icon
                name="arrowRight"
                size={15}
              />
            </Link>
          </div>

          <div className="userPassportHeroCopy">
            <span className="userPassportEyebrow">
              <span />
              Outdoor Passport
            </span>

            <h1>{displayName}</h1>

            <p>
              Outdoor profil sa stvarno posećenim mestima, GPS check-inovima,
              fotografijama i sačuvanim lokacijama.
            </p>
          </div>

        </section>

        <section className="userPassportContent">
          <article className="userPassportIdentityCard">
            <div className="userPassportIdentity">
              <div className="userPassportAvatarWrap">
                <img
                  src={
                    profile.avatar_url ||
                    FALLBACK_AVATAR
                  }
                  alt={displayName}
                  className="userPassportAvatar"
                />

                <span className="userPassportAvatarBadge">
                  <Icon
                    name="sparkle"
                    size={18}
                  />
                </span>
              </div>

              <div className="userPassportIdentityCopy">
                <div className="userPassportBadges">
                  <span className="memberBadge">
                    Outdoor profil
                  </span>

                  <span className="levelBadge">
                    <Icon
                      name="trophy"
                      size={14}
                    />
                    {level.name}
                  </span>
                </div>

                <h2>{displayName}</h2>

                <p className="userPassportUsername">
                  @{profile.username}
                </p>

                <p className="userPassportLocation">
                  <Icon
                    name="mapPin"
                    size={16}
                  />
                  {location ||
                    "Lokacija još nije dodata"}
                </p>
              </div>
            </div>

            {isOwnProfile && (
              <Link
                to="/edit-profile"
                className="userPassportEdit"
              >
                <Icon
                  name="edit"
                  size={17}
                />
                Izmeni profil
              </Link>
            )}
          </article>

          <section className="userPassportStats">
            <article>
              <span className="statIcon green">
                <Icon
                  name="mapPin"
                  size={19}
                />
              </span>

              <div>
                <strong>
                  {formatNumber(
                    visitedPlaces.length
                  )}
                </strong>
                <span>
                  posećenih mesta
                </span>
              </div>
            </article>

            <article>
              <span className="statIcon dark">
                <Icon
                  name="verified"
                  size={19}
                />
              </span>

              <div>
                <strong>
                  {formatNumber(
                    checkins.length
                  )}
                </strong>
                <span>
                  GPS check-inova
                </span>
              </div>
            </article>

            <article>
              <span className="statIcon sand">
                <Icon
                  name="camera"
                  size={19}
                />
              </span>

              <div>
                <strong>
                  {formatNumber(
                    photos.length
                  )}
                </strong>
                <span>
                  fotografija
                </span>
              </div>
            </article>

            <article>
              <span className="statIcon rose">
                <Icon
                  name="heart"
                  size={19}
                />
              </span>

              <div>
                <strong>
                  {formatNumber(
                    savedPlaces.length
                  )}
                </strong>
                <span>
                  sačuvanih mesta
                </span>
              </div>
            </article>
          </section>



          <div className="userPassportGrid">
            <section className="userPassportPanel mapPanel">
              <div className="panelHeader">
                <div>
                  <span className="userPassportSectionLabel">
                    Moja mapa
                  </span>

                  <h3>
                    Moja mapa
                  </h3>

                  <p>
                    Samo javni GPS potvrđeni
                    check-inovi.
                  </p>
                </div>

                <span className="panelIcon">
                  <Icon
                    name="layers"
                    size={21}
                  />
                </span>
              </div>

              <div className="userPassportMap">
                <MapContainer
                  center={mapCenter}
                  zoom={
                    mapPlaces.length > 0
                      ? 7
                      : 6
                  }
                  scrollWheelZoom={false}
                  className="userPassportLeaflet"
                >
                  <TileLayer
                    attribution='&copy; OpenStreetMap contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  {mapPlaces.map((place) => (
                    <Marker
                      key={place.id}
                      position={[
                        Number(
                          place.latitude
                        ),
                        Number(
                          place.longitude
                        ),
                      ]}
                      icon={makePassportMarker(
                        place
                      )}
                      eventHandlers={{
                        click: () =>
                          navigate(
                            `/explore/${place.id}`
                          ),
                      }}
                    />
                  ))}
                </MapContainer>

                <div className="userPassportMapHud">
                  <Icon
                    name="verified"
                    size={14}
                  />
                  GPS VERIFIED ONLY
                </div>
              </div>
            </section>

            <aside className="userPassportPanel aboutPanel">
              <div className="panelHeader">
                <div>
                  <span className="userPassportSectionLabel">
                    O članu
                  </span>

                  <h3>
                    Priča iza profila.
                  </h3>
                </div>

                <span className="panelIcon">
                  <Icon
                    name="user"
                    size={21}
                  />
                </span>
              </div>

              <p className="userPassportBio">
                {profile.bio ||
                  "Ovaj član još nije dodao biografiju. Avanture možda govore više od reči, ali dobar opis ipak pomaže."}
              </p>

              <div className="userPassportActivities">
                <span>
                  Omiljene aktivnosti
                </span>

                {activities.length > 0 ? (
                  <div className="activityChips">
                    {activities.map(
                      (activity) => (
                        <span
                          key={activity}
                        >
                          {activity}
                        </span>
                      )
                    )}
                  </div>
                ) : (
                  <div className="miniEmpty">
                    Aktivnosti još nisu
                    dodate.
                  </div>
                )}
              </div>
            </aside>
          </div>





          <section className="userPassportPanel galleryPanel">
            <div className="panelHeader">
              <div>
                <span className="userPassportSectionLabel">
                  Fotografije
                </span>

                <h3>
                  Mesta kroz njegov/njen objektiv.
                </h3>
              </div>

              <span className="galleryCount">
                {photos.length}
              </span>
            </div>

            {photos.length > 0 ? (
              <div className="userPassportGallery">
                {photos
                  .slice(0, 12)
                  .map((photo, index) => (
                    <button
                      type="button"
                      key={photo.id}
                      className={
                        index === 0
                          ? "featured"
                          : ""
                      }
                      onClick={() =>
                        photo.place_id &&
                        navigate(
                          `/explore/${photo.place_id}`
                        )
                      }
                    >
                      <img
                        src={photo.image_url}
                        alt={
                          photo.places?.name ||
                          "Outdoor fotografija"
                        }
                      />

                      <div>
                        <strong>
                          {photo.places?.name ||
                            "Outdoor mesto"}
                        </strong>

                        <span>
                          {formatDate(
                            photo.created_at
                          )}
                        </span>
                      </div>
                    </button>
                  ))}
              </div>
            ) : (
              <div className="passportEmpty galleryEmpty">
                <Icon
                  name="camera"
                  size={27}
                />

                <strong>
                  Još nema fotografija.
                </strong>

                <span>
                  Kada član doda fotografije
                  na Explore mestu, pojaviće se
                  ovde.
                </span>
              </div>
            )}
          </section>

          <section className="userPassportPanel savedPanel">
            <div className="panelHeader">
              <div>
                <span className="userPassportSectionLabel">
                  Želim da posetim
                </span>

                <h3>
                  Javni outdoor bucket list.
                </h3>
              </div>

              <span className="panelIcon">
                <Icon
                  name="heart"
                  size={21}
                />
              </span>
            </div>

            {savedPlaces.length > 0 ? (
              <div className="savedGrid">
                {savedPlaces
                  .slice(0, 8)
                  .map((item) => (
                    <Link
                      key={item.id}
                      to={`/explore/${item.place_id}`}
                    >
                      <img
                        src={
                          item.places?.cover_url ||
                          FALLBACK_COVER
                        }
                        alt=""
                      />

                      <div>
                        <span>
                          {item.places
                            ?.place_categories
                            ?.name ||
                            "Outdoor"}
                        </span>

                        <strong>
                          {item.places?.name ||
                            "Outdoor mesto"}
                        </strong>

                        <small>
                          {[
                            item.places
                              ?.locality,
                            item.places
                              ?.region,
                          ]
                            .filter(Boolean)
                            .join(" · ") ||
                            "Srbija"}
                        </small>
                      </div>
                    </Link>
                  ))}
              </div>
            ) : (
              <div className="passportEmpty">
                Nema sačuvanih mesta.
              </div>
            )}
          </section>

          <section className="timelineSection">
            <div className="timelineIntro">
              <span className="userPassportSectionLabel">
                Poslednje posete
              </span>

              <h3>
                Poslednja posećena mesta.
              </h3>

              <p>
                Javni GPS potvrđeni check-inovi.
              </p>
            </div>

            <div className="timelineList">
              {recentCheckins.length > 0 ? (
                recentCheckins.map(
                  (item, index) => (
                    <Link
                      to={`/explore/${item.place_id}`}
                      key={item.id}
                    >
                      <span className="timelineIndex">
                        {String(
                          index + 1
                        ).padStart(2, "0")}
                      </span>

                      <span className="timelineLine" />

                      <div>
                        <small>
                          {formatDate(
                            item.visited_at ||
                              item.created_at
                          )}
                        </small>

                        <strong>
                          {item.places?.name ||
                            "Outdoor mesto"}
                        </strong>

                        <p>
                          {[
                            item.places
                              ?.locality,
                            item.places
                              ?.region,
                          ]
                            .filter(Boolean)
                            .join(" · ") ||
                            "Srbija"}
                        </p>
                      </div>

                      <Icon
                        name="arrowRight"
                        size={17}
                      />
                    </Link>
                  )
                )
              ) : (
                <div className="passportEmpty">
                  Još nema javnih GPS
                  potvrđenih poseta.
                </div>
              )}
            </div>
          </section>

          <section className="communityCard">
            <div>
              <span className="userPassportSectionLabel">
                MeetOutdoors zajednica
              </span>

              <h3>
                Ne skupljamo samo lajkove.
                Skupljamo mesta.
              </h3>

              <p>
                Pogledaj Explore mapu i pronađi
                sledeće mesto koje vredi posetiti.
              </p>
            </div>

            <Link to="/explore">
              Istraži mapu
              <Icon
                name="arrowRight"
                size={16}
              />
            </Link>
          </section>
        </section>
      </main>
    </>
  );
}

function UserProfileStyles() {
  return (
    <style>{`
      *{box-sizing:border-box}
      html,body,#root{min-height:100%}
      body{margin:0;background:#e9eee6}
      button{font:inherit}
      button,a{-webkit-tap-highlight-color:transparent}
      .userPassportPage,.userPassportStatePage{min-height:100vh;color:#203229;font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}
      .userPassportPage{padding:96px 28px 70px;background:radial-gradient(circle at 7% 0%,rgba(177,211,139,.18),transparent 27%),radial-gradient(circle at 94% 25%,rgba(64,106,75,.1),transparent 24%),#e9eee6}
      .userPassportPage a{color:inherit;text-decoration:none}
      .userPassportHero{position:relative;isolation:isolate;width:min(1240px,100%);min-height:620px;margin:0 auto;padding:34px;overflow:hidden;border-radius:38px;background-position:center;background-size:cover;color:white;box-shadow:0 38px 100px rgba(23,54,36,.22)}
      .userPassportHero::before{position:absolute;top:-170px;right:-140px;z-index:-1;width:550px;height:550px;border:1px solid rgba(255,255,255,.07);border-radius:50%;content:"";box-shadow:0 0 0 80px rgba(255,255,255,.02),0 0 0 160px rgba(255,255,255,.012)}
      .userPassportHeroGlow{position:absolute;bottom:-120px;left:-70px;width:420px;height:420px;border-radius:50%;background:rgba(186,255,158,.08);filter:blur(70px)}
      .userPassportHeroTop{position:relative;z-index:2;display:flex;align-items:center;justify-content:space-between;gap:20px}
      .userPassportBrand{display:inline-flex;align-items:center;gap:10px;color:white!important;font-weight:900;letter-spacing:-.03em}
      .userPassportBrand>span{display:grid;place-items:center;width:43px;height:43px;border:1px solid rgba(255,255,255,.18);border-radius:14px;background:rgba(255,255,255,.1);color:#cef39a;backdrop-filter:blur(14px)}
      .userPassportExploreLink{display:inline-flex;align-items:center;gap:8px;min-height:42px;padding:0 14px;border:1px solid rgba(255,255,255,.17);border-radius:13px;background:rgba(255,255,255,.1);color:white!important;font-size:10px;font-weight:850;backdrop-filter:blur(14px)}
      .userPassportHeroCopy{position:relative;z-index:2;max-width:800px;padding-top:132px}
      .userPassportEyebrow{display:inline-flex;align-items:center;gap:9px;padding:9px 13px;border:1px solid rgba(255,255,255,.14);border-radius:999px;background:rgba(255,255,255,.07);color:rgba(255,255,255,.76);font-size:9px;font-weight:900;letter-spacing:.12em;text-transform:uppercase}
      .userPassportEyebrow>span{width:7px;height:7px;border-radius:50%;background:#cef39a;box-shadow:0 0 0 5px rgba(206,243,154,.12)}
      .userPassportHeroCopy h1{margin:24px 0 0;font-size:clamp(64px,8vw,106px);line-height:.87;letter-spacing:-.08em}
      .userPassportHeroCopy p{max-width:590px;margin:25px 0 0;color:rgba(255,255,255,.63);font-size:13px;line-height:1.75}
      .userPassportLevelStamp{position:absolute;right:34px;bottom:34px;z-index:2;display:flex;align-items:center;gap:10px;padding:12px 14px;border:1px solid rgba(206,243,154,.18);border-radius:17px;background:rgba(5,18,10,.45);color:#cef39a;backdrop-filter:blur(18px)}
      .userPassportLevelStamp small,.userPassportLevelStamp strong{display:block}
      .userPassportLevelStamp small{font-size:6px;font-weight:900;letter-spacing:.11em}
      .userPassportLevelStamp strong{margin-top:3px;color:white;font-size:11px}
      .userPassportContent{position:relative;z-index:3;width:min(1140px,100%);margin:-74px auto 0}
      .userPassportIdentityCard{display:flex;align-items:flex-end;justify-content:space-between;gap:28px;padding:26px;border:1px solid rgba(221,229,218,.9);border-radius:28px;background:rgba(255,255,255,.9);box-shadow:0 24px 60px rgba(28,49,35,.1);backdrop-filter:blur(22px)}
      .userPassportIdentity{display:flex;align-items:flex-end;gap:22px;min-width:0}
      .userPassportAvatarWrap{position:relative;flex:0 0 auto}
      .userPassportAvatar{display:block;width:150px;height:150px;border:5px solid white;border-radius:38px;object-fit:cover;box-shadow:0 18px 42px rgba(29,50,37,.16)}
      .userPassportAvatarBadge{position:absolute;right:-6px;bottom:7px;display:grid;place-items:center;width:42px;height:42px;border:4px solid white;border-radius:50%;background:#264f36;color:#d7f7a5}
      .userPassportIdentityCopy{min-width:0;padding-bottom:8px}
      .userPassportBadges{display:flex;flex-wrap:wrap;align-items:center;gap:8px}
      .memberBadge,.levelBadge{display:inline-flex;align-items:center;gap:6px;min-height:29px;padding:0 10px;border-radius:999px;font-size:8px;font-weight:900;letter-spacing:.08em;text-transform:uppercase}
      .memberBadge{background:#e7f0dc;color:#5b7840}
      .levelBadge{background:#173b27;color:#d8ffbf}
      .userPassportIdentityCopy h2{margin:13px 0 0;color:#263a2f;font-size:clamp(32px,5vw,50px);line-height:1;letter-spacing:-.06em}
      .userPassportUsername{margin:7px 0 0;color:#87928a;font-size:12px;font-weight:650}
      .userPassportLocation{display:flex;align-items:center;gap:7px;margin:12px 0 0;color:#627168;font-size:10px;font-weight:700}
      .userPassportEdit{display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:45px;padding:0 16px;border:1px solid #244d34;border-radius:14px;background:#183a27;color:white!important;font-size:10px;font-weight:850}
      .userPassportStats{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;margin-top:18px}
      .userPassportStats article{display:grid;grid-template-columns:48px minmax(0,1fr);align-items:center;gap:11px;padding:13px;border:1px solid #dbe4d8;border-radius:18px;background:rgba(255,255,255,.78);box-shadow:0 12px 30px rgba(31,51,38,.045)}
      .statIcon{display:grid;place-items:center;width:48px;height:48px;border-radius:15px}
      .statIcon.green{background:#e7f2df;color:#557c3c}
      .statIcon.dark{background:#173b27;color:#baff9e}
      .statIcon.sand{background:#fff3d9;color:#987329}
      .statIcon.rose{background:#fdebea;color:#a45149}
      .userPassportStats strong,.userPassportStats span{display:block}
      .userPassportStats strong{font-size:22px;letter-spacing:-.04em}
      .userPassportStats article>div>span{margin-top:3px;color:#89938c;font-size:7px;font-weight:800;text-transform:uppercase}
      .userPassportLevel{display:grid;grid-template-columns:minmax(220px,.7fr) minmax(0,1.3fr);align-items:center;gap:30px;margin-top:18px;padding:24px 26px;border-radius:24px;background:linear-gradient(145deg,#0f2d1c,#1d4c31);color:white;box-shadow:0 20px 45px rgba(23,58,39,.14)}
      .userPassportLevel h3{margin:7px 0 0;color:#baff9e;font-size:28px;letter-spacing:-.04em}
      .userPassportLevel p{margin:7px 0 0;color:rgba(255,255,255,.48);font-size:9px}
      .levelProgress>div{height:12px;padding:3px;border:1px solid rgba(255,255,255,.12);border-radius:999px;background:rgba(255,255,255,.06)}
      .levelProgress>div>span{display:block;height:100%;border-radius:999px;background:#baff9e}
      .levelProgress footer{display:flex;align-items:center;justify-content:space-between;margin-top:8px}
      .levelProgress footer span{color:rgba(255,255,255,.45);font-size:7px}
      .levelProgress footer strong{color:#baff9e;font-size:7px;letter-spacing:.08em}
      .userPassportGrid{display:grid;grid-template-columns:minmax(0,1.3fr) minmax(320px,.7fr);gap:18px;margin-top:18px}
      .userPassportPanel,.communityCard,.timelineSection{border:1px solid #dbe4d8;border-radius:26px;background:rgba(255,255,255,.78);box-shadow:0 14px 38px rgba(31,51,38,.05)}
      .userPassportPanel{padding:27px}
      .panelHeader{display:flex;align-items:flex-start;justify-content:space-between;gap:18px}
      .userPassportSectionLabel{color:#789456;font-size:8px;font-weight:900;letter-spacing:.13em;text-transform:uppercase}
      .panelHeader h3,.communityCard h3,.timelineIntro h3{margin:8px 0 0;color:#2f4437;font-size:24px;line-height:1.08;letter-spacing:-.04em}
      .panelHeader p{margin:7px 0 0;color:#8b958e;font-size:8px}
      .panelIcon{display:grid;place-items:center;flex:0 0 auto;width:44px;height:44px;border-radius:14px;background:#e8f0de;color:#608046}
      .userPassportBio{margin:27px 0 0;color:#69766e;font-size:12px;line-height:1.8}
      .userPassportActivities{margin-top:29px;padding-top:22px;border-top:1px solid #e4e9e1}
      .userPassportActivities>span{display:block;margin-bottom:12px;color:#667369;font-size:8px;font-weight:850;letter-spacing:.08em;text-transform:uppercase}
      .activityChips{display:flex;flex-wrap:wrap;gap:8px}
      .activityChips span{padding:9px 12px;border:1px solid #d7e2d1;border-radius:999px;background:#f5f8f2;color:#506557;font-size:8px;font-weight:750}
      .miniEmpty{margin:0;color:#879289;font-size:9px}
      .userPassportMap{position:relative;height:390px;margin-top:18px;overflow:hidden;border-radius:20px}
      .userPassportLeaflet{width:100%;height:100%}
      .passportMarkerShell{background:transparent!important;border:0!important}
      .passportMarker{position:relative;width:42px;height:42px;padding:3px;border:3px solid white;border-radius:14px;background:#173b27;box-shadow:0 12px 26px rgba(20,48,31,.28)}
      .passportMarker img{width:100%;height:100%;border-radius:9px;object-fit:cover}
      .passportMarker span{position:absolute;bottom:-7px;left:50%;width:14px;height:14px;border-right:3px solid white;border-bottom:3px solid white;background:#173b27;transform:translateX(-50%) rotate(45deg);z-index:-1}
      .userPassportMapHud{position:absolute;right:11px;bottom:11px;z-index:500;display:inline-flex;align-items:center;gap:6px;padding:7px 9px;border:1px solid rgba(255,255,255,.16);border-radius:10px;background:rgba(6,18,10,.72);color:#dfffd1;font-size:6px;font-weight:900;letter-spacing:.07em;backdrop-filter:blur(10px)}
      .collectionsPanel,.badgesPanel,.galleryPanel,.savedPanel{margin-top:18px}
      .collectionsGrid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:9px;margin-top:18px}
      .collectionsGrid article{display:grid;grid-template-columns:42px minmax(0,1fr);align-items:center;gap:9px;padding:11px;border:1px solid #e0e7dd;border-radius:15px;background:#f8faf6}
      .collectionsGrid article>span{display:grid;place-items:center;width:42px;height:42px;border-radius:13px;background:#e9f1e3;color:#608046}
      .collectionsGrid strong,.collectionsGrid small{display:block}
      .collectionsGrid strong{font-size:8px}
      .collectionsGrid small{margin-top:3px;color:#929c95;font-size:6px}
      .achievementsGrid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin-top:18px}
      .achievementsGrid article{padding:15px;border:1px solid #dfe6dc;border-radius:17px;background:linear-gradient(145deg,#f9fbf7,#f3f7ef)}
      .achievementsGrid article>span{display:grid;place-items:center;width:42px;height:42px;border-radius:14px;background:#173b27;color:#baff9e}
      .achievementsGrid strong,.achievementsGrid small{display:block}
      .achievementsGrid strong{margin-top:12px;font-size:9px}
      .achievementsGrid small{margin-top:4px;color:#89938c;font-size:7px;line-height:1.4}
      .galleryCount{display:grid;place-items:center;min-width:38px;height:38px;border-radius:12px;background:#e8f0de;color:#608046;font-size:9px;font-weight:900}
      .userPassportGallery{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:9px;margin-top:18px}
      .userPassportGallery button{position:relative;height:220px;padding:0;overflow:hidden;border:0;border-radius:17px;background:#dce5d8;cursor:pointer}
      .userPassportGallery button.featured{grid-column:span 2;grid-row:span 2;height:449px}
      .userPassportGallery img{width:100%;height:100%;object-fit:cover}
      .userPassportGallery button>div{position:absolute;right:8px;bottom:8px;left:8px;padding:8px;border-radius:11px;background:rgba(5,17,10,.62);color:white;text-align:left;backdrop-filter:blur(10px)}
      .userPassportGallery strong,.userPassportGallery span{display:block}
      .userPassportGallery strong{overflow:hidden;font-size:7px;text-overflow:ellipsis;white-space:nowrap}
      .userPassportGallery span{margin-top:3px;color:rgba(255,255,255,.48);font-size:5px}
      .savedGrid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;margin-top:18px}
      .savedGrid a{overflow:hidden;border:1px solid #dfe6dc;border-radius:17px;background:#fafbf8}
      .savedGrid img{width:100%;height:135px;display:block;object-fit:cover}
      .savedGrid a>div{padding:11px}
      .savedGrid span,.savedGrid strong,.savedGrid small{display:block}
      .savedGrid span{color:#789456;font-size:5px;font-weight:900;text-transform:uppercase}
      .savedGrid strong{margin-top:3px;overflow:hidden;font-size:9px;text-overflow:ellipsis;white-space:nowrap}
      .savedGrid small{margin-top:4px;color:#89938c;font-size:6px}
      .passportEmpty{display:grid;place-items:center;min-height:130px;margin-top:18px;padding:22px;border:1px dashed #ccd7c8;border-radius:18px;background:#f8faf6;color:#879289;font-size:9px;text-align:center}
      .galleryEmpty{gap:7px}
      .galleryEmpty strong{font-size:9px}
      .galleryEmpty span{font-size:7px}
      .timelineSection{display:grid;grid-template-columns:minmax(220px,.35fr) minmax(0,.65fr);gap:24px;margin-top:18px;padding:28px}
      .timelineIntro p{margin:10px 0 0;color:#88938b;font-size:9px;line-height:1.5}
      .timelineList{display:grid;gap:7px}
      .timelineList>a{display:grid;grid-template-columns:38px 1px minmax(0,1fr) auto;align-items:center;gap:11px;padding:10px;border:1px solid #e0e7dd;border-radius:14px;background:#f8faf6}
      .timelineIndex{display:grid;place-items:center;width:38px;height:38px;border-radius:12px;background:#173b27;color:#baff9e;font-size:7px;font-weight:900}
      .timelineLine{width:1px;height:31px;background:#d6dfd2}
      .timelineList small,.timelineList strong,.timelineList p{display:block}
      .timelineList small{color:#98a19a;font-size:5px;text-transform:uppercase}
      .timelineList strong{margin-top:3px;font-size:8px}
      .timelineList p{margin:3px 0 0;color:#7d8981;font-size:6px}
      .communityCard{display:flex;align-items:center;justify-content:space-between;gap:28px;margin-top:18px;padding:30px}
      .communityCard p{max-width:650px;margin:14px 0 0;color:#7d8981;font-size:10px;line-height:1.7}
      .communityCard>a{display:inline-flex;align-items:center;justify-content:center;gap:8px;flex:0 0 auto;min-height:45px;padding:0 16px;border-radius:14px;background:#183a27;color:white!important;font-size:10px;font-weight:850}
      .userPassportStatePage{display:grid;place-items:center;padding:118px 24px 24px;background:radial-gradient(circle at top left,rgba(166,203,126,.18),transparent 30%),#edf1e9}
      .userPassportStateCard{display:grid;place-items:center;width:min(500px,100%);padding:50px 30px;border:1px solid #dce3d9;border-radius:28px;background:rgba(255,255,255,.84);text-align:center;box-shadow:0 20px 60px rgba(28,48,35,.08)}
      .userPassportLoader{width:38px;height:38px;border:3px solid #dce5d7;border-top-color:#52783c;border-radius:50%;animation:userPassportSpin .8s linear infinite}
      @keyframes userPassportSpin{to{transform:rotate(360deg)}}
      .userPassportStateIcon{display:grid;place-items:center;width:62px;height:62px;border-radius:20px;background:#ffe9e5;color:#a85247}
      .userPassportStateCard h1{margin:18px 0 0;color:#263a2f;font-size:28px;letter-spacing:-.04em}
      .userPassportStateCard p{max-width:380px;margin:9px 0 0;color:#7e8981;font-size:11px;line-height:1.65}
      .userPassportStateActions{display:flex;flex-wrap:wrap;justify-content:center;gap:9px;margin-top:20px}
      .userPassportStateActions button,.userPassportStateActions a{display:inline-flex;align-items:center;justify-content:center;gap:7px;min-height:42px;padding:0 14px;border-radius:12px;cursor:pointer;font-size:10px;font-weight:850}
      .userPassportStateActions button{border:0;background:#183a27;color:white}
      .userPassportStateActions a{border:1px solid #d5ded2;background:white;color:#51665a;text-decoration:none}

      @media(max-width:980px){
        .userPassportIdentityCard{align-items:flex-start;flex-direction:column}
        .userPassportStats{grid-template-columns:repeat(2,minmax(0,1fr))}
        .userPassportGrid,.timelineSection{grid-template-columns:1fr}
        .collectionsGrid,.savedGrid{grid-template-columns:repeat(2,minmax(0,1fr))}
        .achievementsGrid{grid-template-columns:repeat(2,minmax(0,1fr))}
      }

      @media(max-width:700px){
        .userPassportPage{padding:78px 0 56px}
        .userPassportHero{min-height:650px;padding:22px;border-radius:0 0 32px 32px}
        .userPassportHeroCopy{padding-top:140px}
        .userPassportLevelStamp{right:22px;bottom:22px}
        .userPassportContent{padding:0 12px}
        .userPassportIdentity{align-items:flex-start;flex-direction:column}
        .userPassportAvatar{width:132px;height:132px;border-radius:34px}
        .userPassportStats{grid-template-columns:1fr 1fr}
        .userPassportLevel{grid-template-columns:1fr}
        .userPassportGallery{grid-template-columns:repeat(2,minmax(0,1fr))}
        .userPassportGallery button.featured{grid-column:1/-1;grid-row:auto;height:320px}
        .communityCard{align-items:flex-start;flex-direction:column}
      }

      @media(max-width:480px){
        .userPassportHero{min-height:610px;padding:18px}
        .userPassportHeroCopy h1{font-size:52px}
        .userPassportExploreLink{width:42px;padding:0;justify-content:center;font-size:0}
        .userPassportLevelStamp{left:18px;right:auto}
        .userPassportContent{padding:0 9px}
        .userPassportIdentityCard,.userPassportPanel,.timelineSection,.communityCard{padding:18px}
        .userPassportEdit{width:100%}
        .userPassportStats{grid-template-columns:1fr}
        .collectionsGrid,.savedGrid,.achievementsGrid{grid-template-columns:1fr}
        .userPassportMap{height:300px}
        .userPassportGallery{grid-template-columns:1fr}
        .userPassportGallery button,.userPassportGallery button.featured{grid-column:auto;height:250px}
      }


      /* USER PROFILE — ULTRA COMPACT */
      .userPassportPage{padding:88px 18px 40px}
      .userPassportHero{width:min(1380px,100%);min-height:470px;padding:24px;border-radius:28px}
      .userPassportHeroCopy{max-width:760px;padding-top:64px}
      .userPassportHeroCopy h1{margin-top:14px;font-size:clamp(46px,6vw,78px);line-height:.9}
      .userPassportHeroCopy p{margin-top:11px;max-width:620px;font-size:9px;line-height:1.45}
      .userPassportBrand>span{width:34px;height:34px;border-radius:10px}
      .userPassportExploreLink{min-height:34px;padding:0 10px;border-radius:10px;font-size:8px}
      .userPassportLevelStamp{right:24px;bottom:24px;padding:8px 10px;border-radius:12px}
      .userPassportContent{width:min(1240px,100%);margin:-52px auto 0}
      .userPassportIdentityCard{gap:16px;padding:16px;border-radius:18px}
      .userPassportIdentity{gap:13px}
      .userPassportAvatar{width:112px;height:112px;border-width:4px;border-radius:26px}
      .userPassportAvatarBadge{width:34px;height:34px;border-width:3px}
      .userPassportIdentityCopy h2{margin-top:8px;font-size:clamp(28px,4vw,42px)}
      .userPassportUsername{margin-top:4px;font-size:9px}
      .userPassportLocation{margin-top:7px;font-size:8px}
      .userPassportEdit{min-height:38px;padding:0 12px;border-radius:10px;font-size:8px}
      .memberBadge,.levelBadge{min-height:24px;padding:0 8px;font-size:6.5px}

      .userPassportStats{grid-template-columns:repeat(4,minmax(0,1fr));gap:7px;margin-top:9px}
      .userPassportStats article{grid-template-columns:36px minmax(0,1fr);gap:7px;padding:8px;border-radius:12px}
      .statIcon{width:36px;height:36px;border-radius:10px}
      .userPassportStats strong{font-size:16px}
      .userPassportStats article>div>span{font-size:5.5px}

      .userPassportLevel{grid-template-columns:minmax(170px,.55fr) minmax(0,1.45fr);gap:18px;margin-top:9px;padding:14px 16px;border-radius:16px}
      .userPassportLevel h3{margin-top:4px;font-size:20px}
      .userPassportLevel p{margin-top:4px;font-size:7px}
      .levelProgress>div{height:9px;padding:2px}
      .levelProgress footer{margin-top:5px}

      .userPassportGrid{grid-template-columns:minmax(0,1.4fr) minmax(260px,.6fr);gap:9px;margin-top:9px}
      .userPassportPanel,.communityCard,.timelineSection{border-radius:17px}
      .userPassportPanel{padding:14px}
      .panelHeader{gap:10px}
      .userPassportSectionLabel{font-size:6px}
      .panelHeader h3,.communityCard h3,.timelineIntro h3{margin-top:4px;font-size:18px}
      .panelHeader p{margin-top:3px;font-size:6.5px}
      .panelIcon{width:32px;height:32px;border-radius:9px}
      .userPassportBio{margin-top:11px;font-size:8px;line-height:1.5}
      .userPassportActivities{margin-top:11px;padding-top:9px}
      .userPassportActivities>span{margin-bottom:6px;font-size:6px}
      .activityChips{gap:4px}
      .activityChips span{padding:6px 8px;font-size:6.5px}
      .userPassportMap{height:300px;margin-top:9px;border-radius:13px}

      .collectionsPanel,.badgesPanel,.galleryPanel,.savedPanel{margin-top:9px}
      .collectionsGrid{grid-template-columns:repeat(4,minmax(0,1fr));gap:5px;margin-top:9px}
      .collectionsGrid article{grid-template-columns:31px minmax(0,1fr);gap:6px;padding:7px;border-radius:10px}
      .collectionsGrid article>span{width:31px;height:31px;border-radius:9px}
      .achievementsGrid{grid-template-columns:repeat(3,minmax(0,1fr));gap:6px;margin-top:9px}
      .achievementsGrid article{padding:9px;border-radius:11px}
      .achievementsGrid article>span{width:32px;height:32px;border-radius:10px}
      .achievementsGrid strong{margin-top:7px;font-size:7.5px}
      .achievementsGrid small{font-size:6px}

      .userPassportGallery{grid-template-columns:repeat(5,minmax(0,1fr));gap:5px;margin-top:9px}
      .userPassportGallery button{height:135px;border-radius:11px}
      .userPassportGallery button.featured{grid-column:span 2;grid-row:span 2;height:275px}

      .savedGrid{grid-template-columns:repeat(4,minmax(0,1fr));gap:6px;margin-top:9px}
      .savedGrid a{border-radius:11px}
      .savedGrid img{height:110px}
      .savedGrid a>div{padding:7px}

      .passportEmpty{min-height:90px;margin-top:9px;padding:14px;border-radius:12px}
      .timelineSection{grid-template-columns:minmax(180px,.3fr) minmax(0,.7fr);gap:14px;margin-top:9px;padding:14px}
      .timelineIntro p{margin-top:5px;font-size:7px}
      .timelineList{gap:4px;max-height:320px;overflow:auto;padding-right:2px}
      .timelineList>a{grid-template-columns:30px 1px minmax(0,1fr) auto;gap:7px;padding:7px;border-radius:10px}
      .timelineIndex{width:30px;height:30px;border-radius:9px}
      .communityCard{gap:14px;margin-top:9px;padding:14px}
      .communityCard p{margin-top:5px;font-size:7px;line-height:1.4}

      @media(max-width:1180px){
        .userPassportGallery{grid-template-columns:repeat(4,minmax(0,1fr))}
        .savedGrid{grid-template-columns:repeat(3,minmax(0,1fr))}
      }

      @media(max-width:760px){
        .userPassportPage{padding:72px 0 64px}
        .userPassportHero{min-height:500px;padding:16px;border-radius:0 0 22px 22px}
        .userPassportHeroCopy{padding-top:52px}
        .userPassportHeroCopy h1{font-size:42px}
        .userPassportContent{padding:0 8px;margin:-40px auto 0}
        .userPassportIdentityCard{align-items:center;padding:10px}
        .userPassportAvatar{width:82px;height:82px;border-radius:20px}
        .userPassportIdentityCopy h2{font-size:30px}
        .userPassportEdit{min-width:38px;padding:0 9px;font-size:0}
        .userPassportStats{gap:5px}
        .userPassportStats article{grid-template-columns:1fr;justify-items:center;text-align:center;gap:4px;padding:7px 4px}
        .statIcon{width:30px;height:30px}
        .userPassportLevel{grid-template-columns:1fr;gap:8px;padding:10px}
        .userPassportLevel p{display:none}
        .userPassportGrid{grid-template-columns:1fr;gap:7px}
        .aboutPanel{display:grid;grid-template-columns:1.25fr .75fr;gap:10px}
        .aboutPanel .panelHeader{grid-column:1/-1}
        .userPassportBio{margin-top:0}
        .userPassportActivities{margin-top:0;padding-top:0;border-top:0}
        .userPassportMap{height:230px}
        .collectionsGrid{grid-template-columns:repeat(2,minmax(0,1fr))}
        .achievementsGrid{grid-template-columns:repeat(3,minmax(0,1fr))}
        .userPassportGallery{grid-template-columns:repeat(4,minmax(0,1fr))}
        .userPassportGallery button{height:105px}
        .userPassportGallery button.featured{height:215px}
        .savedGrid{grid-template-columns:repeat(2,minmax(0,1fr));max-height:440px;overflow:auto}
        .savedGrid img{height:95px}
        .timelineSection{grid-template-columns:1fr;gap:8px}
        .timelineIntro p{display:none}
        .timelineList{max-height:300px}
        .communityCard p{display:none}
      }

      @media(max-width:520px){
        .userPassportHero{min-height:470px}
        .userPassportHeroCopy{padding-top:44px}
        .userPassportHeroCopy h1{font-size:36px}
        .userPassportAvatar{width:72px;height:72px;border-radius:18px}
        .statIcon{display:none}
        .aboutPanel{grid-template-columns:1fr}
        .aboutPanel .panelHeader{grid-column:auto}
        .userPassportActivities{padding-top:7px;border-top:1px solid #e4e9e1}
        .userPassportMap{height:210px}
        .userPassportGallery{display:flex;gap:5px;overflow-x:auto;padding-bottom:4px;scroll-snap-type:x proximity}
        .userPassportGallery button,.userPassportGallery button.featured{flex:0 0 150px;height:105px;scroll-snap-align:start}
        .savedGrid{max-height:390px}
        .timelineList{max-height:280px}
      }



      /* =========================================================
         USER PROFILE — UX REBUILD / CLEAN COMPACT
         Readable, no overlap, calmer hierarchy, mobile-first.
         ========================================================= */
      .userPassportPage{
        overflow-x:hidden;
      }

      .userPassportContent,
      .userPassportIdentityCard,
      .userPassportIdentity,
      .userPassportIdentityCopy,
      .userPassportGrid,
      .userPassportPanel,
      .timelineSection,
      .communityCard{
        min-width:0;
      }

      .userPassportIdentityCopy h2,
      .userPassportUsername,
      .userPassportLocation,
      .panelHeader h3,
      .communityCard h3,
      .timelineIntro h3{
        overflow-wrap:anywhere;
      }

      .userPassportHeroCopy p{font-size:12px;line-height:1.55}
      .userPassportUsername{font-size:11px}
      .userPassportLocation{font-size:10px}
      .memberBadge,.levelBadge{font-size:8px}
      .userPassportStats article>div>span{font-size:8px;line-height:1.2}
      .userPassportSectionLabel{font-size:8px}
      .panelHeader p{font-size:9px;line-height:1.4}
      .userPassportBio{font-size:11px;line-height:1.55}
      .userPassportActivities>span{font-size:8px}
      .activityChips span{font-size:8px}
      .achievementsGrid strong{font-size:10px}
      .achievementsGrid small{font-size:8px;line-height:1.25}
      .timelineIntro p,.communityCard p{font-size:9px;line-height:1.45}

      @media(max-width:760px){
        .userPassportPage{
          padding:64px 0 42px;
        }

        .userPassportHero{
          min-height:350px;
          padding:14px 14px 58px;
          border-radius:0 0 24px 24px;
        }

        .userPassportHeroTop{
          align-items:center;
          gap:8px;
        }

        .userPassportBrand{
          min-width:0;
          font-size:10px;
        }

        .userPassportBrand>span{
          width:30px;
          height:30px;
          flex:0 0 30px;
        }

        .userPassportExploreLink{
          min-height:30px;
          padding:0 9px;
          font-size:8px;
        }

        .userPassportHeroCopy{
          max-width:82%;
          padding-top:38px;
        }

        .userPassportHeroCopy h1{
          margin-top:10px;
          font-size:clamp(34px,11vw,48px);
          line-height:.9;
        }

        .userPassportHeroCopy p{
          max-width:420px;
          margin-top:10px;
          font-size:10px;
          line-height:1.45;
        }

        .userPassportLevelStamp{
          right:14px;
          bottom:14px;
          left:auto;
          max-width:150px;
          padding:7px 9px;
          border-radius:11px;
        }

        .userPassportLevelStamp small{font-size:6px}
        .userPassportLevelStamp strong{font-size:11px}

        .userPassportContent{
          width:100%;
          margin:-42px auto 0;
          padding:0 8px;
        }

        .userPassportIdentityCard{
          display:grid;
          grid-template-columns:minmax(0,1fr) auto;
          align-items:center;
          gap:8px;
          padding:10px;
          border-radius:17px;
        }

        .userPassportIdentity{
          display:grid;
          grid-template-columns:68px minmax(0,1fr);
          align-items:center;
          gap:9px;
          min-width:0;
        }

        .userPassportAvatarWrap{
          width:68px;
          min-width:68px;
        }

        .userPassportAvatar{
          width:68px;
          height:68px;
          border-width:3px;
          border-radius:18px;
        }

        .userPassportAvatarBadge{
          width:25px;
          height:25px;
          border-width:2px;
        }

        .userPassportIdentityCopy{
          min-width:0;
        }

        .userPassportBadges{
          display:flex;
          flex-wrap:wrap;
          gap:4px;
        }

        .memberBadge,.levelBadge{
          min-height:19px;
          padding:0 6px;
          font-size:6.5px;
          white-space:nowrap;
        }

        .userPassportIdentityCopy h2{
          margin-top:5px;
          max-width:100%;
          font-size:clamp(20px,6.4vw,28px);
          line-height:1;
        }

        .userPassportUsername{
          margin-top:3px;
          font-size:8px;
        }

        .userPassportLocation{
          display:flex;
          min-width:0;
          margin-top:5px;
          font-size:7.5px;
          line-height:1.2;
        }

        .userPassportLocation svg{
          flex:0 0 auto;
          width:12px;
          height:12px;
        }

        .userPassportEdit{
          width:34px;
          min-width:34px;
          height:34px;
          min-height:34px;
          padding:0;
          border-radius:10px;
          font-size:0;
        }

        .userPassportStats{
          grid-template-columns:repeat(4,minmax(0,1fr));
          gap:4px;
          margin-top:6px;
        }

        .userPassportStats article{
          display:flex;
          min-width:0;
          min-height:54px;
          padding:7px 3px;
          border-radius:11px;
        }

        .userPassportStats strong{
          font-size:15px;
          line-height:1;
        }

        .userPassportStats article>div>span{
          display:block;
          margin-top:4px;
          font-size:6px;
          line-height:1.1;
        }

        .statIcon{display:none}

        .userPassportLevel{
          display:grid;
          grid-template-columns:auto minmax(0,1fr);
          align-items:center;
          gap:10px;
          margin-top:6px;
          padding:9px 10px;
          border-radius:13px;
        }

        .userPassportLevel h3{
          margin-top:2px;
          font-size:15px;
        }

        .userPassportLevel p{display:none}
        .levelProgress footer{font-size:7px}

        .userPassportGrid{
          grid-template-columns:1fr;
          gap:6px;
          margin-top:6px;
        }

        .userPassportPanel,
        .timelineSection,
        .communityCard{
          padding:10px;
          border-radius:14px;
        }

        .panelHeader{
          align-items:flex-start;
          gap:8px;
        }

        .panelHeader h3,.communityCard h3,.timelineIntro h3{
          margin-top:3px;
          font-size:15px;
          line-height:1.1;
        }

        .panelHeader p{
          margin-top:3px;
          font-size:7.5px;
        }

        .panelIcon{
          width:29px;
          height:29px;
          flex:0 0 29px;
        }

        .aboutPanel{
          display:block;
        }

        .userPassportBio{
          margin-top:8px;
          font-size:9px;
          line-height:1.45;
        }

        .userPassportActivities{
          margin-top:8px;
          padding-top:7px;
          border-top:1px solid #e4e9e1;
        }

        .userPassportActivities>span{font-size:7px}
        .activityChips{gap:4px}
        .activityChips span{padding:5px 7px;font-size:7px}

        .userPassportMap{
          height:205px;
          margin-top:7px;
          border-radius:11px;
        }

        .collectionsPanel,.badgesPanel,.galleryPanel,.savedPanel{
          margin-top:6px;
        }

        .collectionsGrid{
          grid-template-columns:repeat(2,minmax(0,1fr));
          gap:4px;
          margin-top:7px;
        }

        .collectionsGrid article{
          min-width:0;
          padding:6px;
        }

        .achievementsGrid{
          grid-template-columns:repeat(2,minmax(0,1fr));
          gap:4px;
          margin-top:7px;
        }

        .achievementsGrid article{
          min-width:0;
          padding:7px;
        }

        .achievementsGrid strong{font-size:8px}
        .achievementsGrid small{font-size:6.5px}

        .userPassportGallery{
          display:flex;
          gap:5px;
          overflow-x:auto;
          padding:0 0 4px;
          scroll-snap-type:x proximity;
          scrollbar-width:none;
        }

        .userPassportGallery::-webkit-scrollbar{display:none}

        .userPassportGallery button,
        .userPassportGallery button.featured{
          flex:0 0 132px;
          width:132px;
          height:94px;
          grid-column:auto;
          grid-row:auto;
          border-radius:10px;
          scroll-snap-align:start;
        }

        .savedGrid{
          display:flex;
          gap:5px;
          max-height:none;
          overflow-x:auto;
          overflow-y:hidden;
          margin-top:7px;
          padding-bottom:4px;
          scrollbar-width:none;
          scroll-snap-type:x proximity;
        }

        .savedGrid::-webkit-scrollbar{display:none}

        .savedGrid a{
          flex:0 0 145px;
          min-width:145px;
          scroll-snap-align:start;
        }

        .savedGrid img{height:88px}

        .timelineSection{
          grid-template-columns:1fr;
          gap:7px;
          margin-top:6px;
        }

        .timelineIntro p{display:none}

        .timelineList{
          max-height:245px;
          gap:3px;
          overflow-y:auto;
        }

        .timelineList>a{
          grid-template-columns:27px 1px minmax(0,1fr) auto;
          gap:6px;
          padding:6px;
        }

        .timelineIndex{
          width:27px;
          height:27px;
        }

        .communityCard{
          margin-top:6px;
          gap:8px;
        }

        .communityCard p{display:none}
      }

      @media(max-width:380px){
        .userPassportHero{min-height:330px}
        .userPassportHeroCopy{max-width:88%;padding-top:30px}
        .userPassportHeroCopy h1{font-size:32px}
        .userPassportIdentity{grid-template-columns:60px minmax(0,1fr)}
        .userPassportAvatarWrap{width:60px;min-width:60px}
        .userPassportAvatar{width:60px;height:60px}
        .userPassportIdentityCopy h2{font-size:19px}
        .userPassportStats article>div>span{font-size:5.5px}
        .userPassportLevel{grid-template-columns:1fr}
        .levelProgress{width:100%}
      }

      @media(prefers-reduced-motion:reduce){
        *,*::before,*::after{
          animation:none!important;
          transition:none!important;
          scroll-behavior:auto!important
        }
      }

      /* =========================================================
         USER PROFILE V2 — CLEAN OUTDOOR PASSPORT
         Map-first, identity-first, less gamification.
         ========================================================= */

      .userPassportPage{
        padding:82px 24px 64px!important;
        background:
          radial-gradient(circle at 8% 0%,rgba(54,96,67,.08),transparent 28rem),
          #f3f5f1!important;
      }

      .userPassportHero{
        width:min(1240px,100%)!important;
        min-height:360px!important;
        padding:28px!important;
        border-radius:32px!important;
        background-position:center!important;
        box-shadow:0 24px 70px rgba(22,48,31,.14)!important;
      }

      .userPassportHero::before,
      .userPassportHeroGlow{
        display:none!important;
      }

      .userPassportHeroCopy{
        max-width:680px!important;
        padding-top:100px!important;
      }

      .userPassportEyebrow{
        padding:7px 10px!important;
        border-radius:999px!important;
        font-size:8px!important;
      }

      .userPassportHeroCopy h1{
        margin:16px 0 0!important;
        font-size:clamp(42px,6vw,72px)!important;
        line-height:.95!important;
        letter-spacing:-.06em!important;
      }

      .userPassportHeroCopy p{
        max-width:560px!important;
        margin-top:16px!important;
        font-size:12px!important;
        line-height:1.65!important;
      }

      .userPassportContent{
        width:min(1160px,100%)!important;
        margin:-54px auto 0!important;
      }

      .userPassportIdentityCard{
        align-items:center!important;
        padding:22px!important;
        border-radius:24px!important;
        background:rgba(255,255,255,.96)!important;
        box-shadow:0 18px 48px rgba(28,49,35,.09)!important;
      }

      .userPassportIdentity{
        align-items:center!important;
        gap:18px!important;
      }

      .userPassportAvatar{
        width:112px!important;
        height:112px!important;
        border-radius:28px!important;
      }

      .userPassportAvatarBadge{
        width:34px!important;
        height:34px!important;
        right:-4px!important;
        bottom:4px!important;
      }

      .userPassportIdentityCopy{
        padding-bottom:0!important;
      }

      .userPassportIdentityCopy h2{
        margin-top:9px!important;
        font-size:clamp(28px,4vw,42px)!important;
      }

      .userPassportUsername{
        margin-top:5px!important;
      }

      .userPassportLocation{
        margin-top:8px!important;
      }

      .userPassportStats{
        grid-template-columns:repeat(4,minmax(0,1fr))!important;
        gap:9px!important;
        margin-top:14px!important;
      }

      .userPassportStats article{
        grid-template-columns:40px minmax(0,1fr)!important;
        gap:9px!important;
        padding:11px!important;
        border-radius:15px!important;
        background:#fff!important;
        box-shadow:none!important;
      }

      .statIcon{
        width:40px!important;
        height:40px!important;
        border-radius:12px!important;
      }

      .userPassportStats strong{
        font-size:19px!important;
      }

      .userPassportStats article>div>span{
        font-size:6.5px!important;
      }

      .userPassportGrid{
        grid-template-columns:minmax(0,1.45fr) minmax(280px,.55fr)!important;
        gap:14px!important;
        margin-top:16px!important;
      }

      .userPassportPanel,
      .communityCard,
      .timelineSection{
        border:1px solid #dfe5df!important;
        border-radius:22px!important;
        background:#fff!important;
        box-shadow:none!important;
      }

      .userPassportPanel{
        padding:22px!important;
      }

      .panelHeader h3,
      .communityCard h3,
      .timelineIntro h3{
        font-size:22px!important;
      }

      .panelIcon{
        width:40px!important;
        height:40px!important;
        border-radius:12px!important;
      }

      .userPassportMap{
        height:430px!important;
        margin-top:15px!important;
        border-radius:18px!important;
      }

      .aboutPanel{
        min-height:100%!important;
      }

      .userPassportBio{
        margin-top:20px!important;
        font-size:11px!important;
        line-height:1.75!important;
      }

      .userPassportActivities{
        margin-top:22px!important;
        padding-top:18px!important;
      }

      .activityChips{
        gap:6px!important;
      }

      .activityChips span{
        padding:7px 10px!important;
        border-radius:999px!important;
        font-size:7.5px!important;
      }

      .galleryPanel,
      .savedPanel{
        margin-top:16px!important;
      }

      .userPassportGallery{
        gap:8px!important;
        margin-top:15px!important;
      }

      .userPassportGallery button{
        height:190px!important;
        border-radius:14px!important;
      }

      .userPassportGallery button.featured{
        height:388px!important;
      }

      .savedGrid{
        grid-template-columns:repeat(4,minmax(0,1fr))!important;
        gap:9px!important;
        margin-top:15px!important;
      }

      .savedGrid a{
        border-radius:14px!important;
      }

      .savedGrid img{
        height:120px!important;
      }

      .timelineSection{
        grid-template-columns:minmax(190px,.32fr) minmax(0,.68fr)!important;
        gap:20px!important;
        margin-top:16px!important;
        padding:22px!important;
      }

      .timelineList>a{
        padding:9px!important;
        border-radius:12px!important;
      }

      .communityCard{
        margin-top:16px!important;
        padding:24px!important;
      }

      /* Hide legacy gamification if stale markup survives. */
      .userPassportLevel,
      .collectionsPanel,
      .badgesPanel,
      .userPassportLevelStamp{
        display:none!important;
      }

      @media(max-width:900px){
        .userPassportGrid{
          grid-template-columns:1fr!important;
        }

        .userPassportStats{
          grid-template-columns:repeat(2,minmax(0,1fr))!important;
        }

        .savedGrid{
          grid-template-columns:repeat(2,minmax(0,1fr))!important;
        }

        .timelineSection{
          grid-template-columns:1fr!important;
        }
      }

      @media(max-width:680px){
        .userPassportPage{
          padding:68px 10px 36px!important;
        }

        .userPassportHero{
          min-height:285px!important;
          padding:18px!important;
          border-radius:24px!important;
        }

        .userPassportHeroTop{
          gap:10px!important;
        }

        .userPassportBrand{
          font-size:11px!important;
        }

        .userPassportBrand>span{
          width:36px!important;
          height:36px!important;
          border-radius:11px!important;
        }

        .userPassportExploreLink{
          min-height:38px!important;
          padding:0 10px!important;
          font-size:8px!important;
        }

        .userPassportHeroCopy{
          padding-top:72px!important;
        }

        .userPassportHeroCopy h1{
          font-size:40px!important;
        }

        .userPassportHeroCopy p{
          max-width:92%!important;
          font-size:10px!important;
        }

        .userPassportContent{
          margin-top:-34px!important;
        }

        .userPassportIdentityCard{
          padding:15px!important;
          border-radius:19px!important;
          align-items:flex-start!important;
        }

        .userPassportIdentity{
          align-items:flex-start!important;
          gap:12px!important;
        }

        .userPassportAvatar{
          width:78px!important;
          height:78px!important;
          border-width:4px!important;
          border-radius:20px!important;
        }

        .userPassportAvatarBadge{
          width:27px!important;
          height:27px!important;
          border-width:3px!important;
        }

        .userPassportIdentityCopy h2{
          font-size:26px!important;
        }

        .userPassportBadges{
          gap:5px!important;
        }

        .memberBadge,
        .levelBadge{
          min-height:24px!important;
          padding:0 7px!important;
          font-size:6.5px!important;
        }

        .userPassportEdit{
          min-height:38px!important;
          padding:0 10px!important;
          font-size:8px!important;
        }

        .userPassportStats{
          grid-template-columns:repeat(2,minmax(0,1fr))!important;
          gap:7px!important;
          margin-top:10px!important;
        }

        .userPassportStats article{
          grid-template-columns:34px minmax(0,1fr)!important;
          padding:9px!important;
          border-radius:13px!important;
        }

        .statIcon{
          width:34px!important;
          height:34px!important;
          border-radius:10px!important;
        }

        .userPassportStats strong{
          font-size:17px!important;
        }

        .userPassportGrid{
          gap:10px!important;
          margin-top:10px!important;
        }

        .userPassportPanel{
          padding:16px!important;
          border-radius:18px!important;
        }

        .userPassportMap{
          height:320px!important;
          border-radius:15px!important;
        }

        .panelHeader h3,
        .communityCard h3,
        .timelineIntro h3{
          font-size:19px!important;
        }

        .galleryPanel,
        .savedPanel,
        .timelineSection,
        .communityCard{
          margin-top:10px!important;
        }

        .userPassportGallery{
          grid-template-columns:repeat(2,minmax(0,1fr))!important;
        }

        .userPassportGallery button,
        .userPassportGallery button.featured{
          grid-column:auto!important;
          grid-row:auto!important;
          height:150px!important;
        }

        .savedGrid{
          grid-template-columns:repeat(2,minmax(0,1fr))!important;
        }

        .savedGrid img{
          height:100px!important;
        }

        .timelineSection{
          padding:16px!important;
        }

        .communityCard{
          padding:18px!important;
          gap:16px!important;
          align-items:flex-start!important;
          flex-direction:column!important;
        }
      }

    `}</style>
  );
}
